/**
 * Field normalization utilities for discipline service
 * Handles backward compatibility and field standardization
 */

import { consequenceTypesService } from '../../consequenceTypesService.js';
import { typeNormalizer } from './TypeNormalizer.js';

/**
 * Get offence field with compatibility for old field names
 * Supports reading from: offence, offense, reason, offenseName
 * @param {Object} data - Document data
 * @returns {string} The offence text
 */
export function getOffenceField(data) {
  return data?.offence || data?.offense || data?.reason || data?.offenseName || 'Unknown';
}

/**
 * Normalize document fields for consistent access
 * Provides compatibility layer for old field names
 * @param {Object} data - Raw document data
 * @returns {Object} Normalized document data
 */
export function normalizeDocumentFields(data) {
  if (!data) return data;

  const normalized = { ...data };

  // Normalize to standard 'offence' field (British spelling)
  if (!normalized.offence && (normalized.offense || normalized.reason || normalized.offenseName)) {
    normalized.offence = normalized.offense || normalized.reason || normalized.offenseName;
    // Remove old fields to prevent duplicates
    delete normalized.offense;
    delete normalized.reason;
    delete normalized.offenseName;
  }

  // Normalize other common variations
  if (!normalized.createdAt && normalized.timestamp) {
    normalized.createdAt = normalized.timestamp;
  }

  if (!normalized.offenseCategory && normalized.offenceCategory) {
    normalized.offenseCategory = normalized.offenceCategory;
  }

  return normalized;
}

/**
 * Helper to generate class ID from name with robust sanitization
 * @param {string} className - The class name to convert
 * @returns {string|null} Sanitized class ID
 */
export function generateClassId(className) {
  if (!className) return null;
  return className
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')  // Remove ALL special chars, not just spaces
    .replace(/^_|_$/g, '');       // Trim underscores from ends
}

// Cache for valid types to avoid repeated Firestore calls
const validTypesCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get valid consequence types from Firestore (with caching)
 * @param {string} schoolId - School ID for fetching consequence types
 * @returns {Promise<Array<string>>} Array of valid type values
 */
async function getValidTypes(schoolId) {
  const now = Date.now();
  const cached = validTypesCache.get(schoolId);

  if (cached && (now - cached.timestamp < CACHE_DURATION)) {
    return cached.values;
  }

  if (!schoolId) {
    throw new Error('A school is required to validate the consequence type.');
  }
  const types = await consequenceTypesService.getAll(schoolId);
  const values = types.map(type => type.value).filter(Boolean);
  validTypesCache.set(schoolId, {values, timestamp: now});
  return values;
}

/**
 * Validates and standardizes discipline record data
 * Ensures all records use consistent field names and have required fields
 * @param {Object} data - The discipline record data to validate
 * @returns {Promise<Object>} Validated and standardized data
 */
export async function validateDisciplineRecord(data) {
  // Use TypeNormalizer to normalize the type before validation
  const normalizedType = await typeNormalizer.normalize(data.type, data.schoolId);
  const validTypes = await getValidTypes(data.schoolId);

  // Validate normalized type
  if (!validTypes.includes(normalizedType)) {
    // Try to map deprecated types
    if (data.type === 'consequence' || data.type === 'mention-rule-consequence') {
      data.type = data.consequenceType || 'demerit';
      console.log(`Mapped deprecated type to: ${data.type}`);
      if (!validTypes.includes(data.type)) {
        throw new Error(`The mapped consequence type "${data.type}" is not configured for this school.`);
      }
    } else {
      throw new Error(`The consequence type "${data.type}" is not configured for this school.`);
    }
  } else if (normalizedType !== data.type) {
    // Update type to normalized value
    console.log(`Normalized type from "${data.type}" to "${normalizedType}"`);
    data.type = normalizedType;
  }

  // Standardize to British spelling 'offence'
  if (!data.offence) {
    if (data.offense || data.reason || data.offenseName) {
      data.offence = data.offense || data.reason || data.offenseName;
      // Clean up old fields to prevent duplicates
      delete data.offense;
      delete data.reason;
      delete data.offenseName;
    } else if (data.consequenceDetails) {
      // Auto-generated rule consequences carry their text in consequenceDetails
      data.offence = data.consequenceDetails;
    } else {
      // Refuse to silently sentinel-stamp. Surfaces upstream bugs (e.g. modal
      // submitting empty offence) instead of writing 'Unknown offence' forever.
      throw new Error(
        `Discipline record is missing offence. type=${data.type} studentId=${data.studentId} createdBy=${data.createdBy}`
      );
    }
  }

  return data;
}
